import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { candidatesAPI, screeningAPI, i9API } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// ── Required doc types per visa (mirrors PublicDocUpload VISA_DOCS) ──────────
const COMMON_DOCS = ['driving_license', 'passport', 'ssn_card', 'visa_copy', 'i94'];
const VISA_REQUIRED_DOCS = {
  OPT:         [...COMMON_DOCS, 'ead_card', 'i20_certificate', 'degree_bachelors'],
  STEM_OPT:    [...COMMON_DOCS, 'ead_card', 'i20_certificate', 'i983_form', 'degree_bachelors'],
  CPT:         [...COMMON_DOCS, 'i20_certificate', 'degree_bachelors'],
  H1B:         [...COMMON_DOCS, 'i797', 'degree_bachelors'],
  H4_EAD:      [...COMMON_DOCS, 'ead_card', 'degree_bachelors'],
  GC:          ['driving_license', 'passport', 'ssn_card', 'gc_card', 'degree_bachelors'],
  CITIZENSHIP: ['driving_license', 'passport', 'ssn_card', 'degree_bachelors'],
  Other:       [...COMMON_DOCS, 'degree_bachelors'],
};
// degree_masters is optional — not required for status transition
const DOC_LABEL = {
  driving_license:    'Driving License',
  passport:           'Passport',
  visa_copy:          'US Visa',
  i94:                'I-94 Arrival Record',
  ssn_card:           'Social Security Card',
  ead_card:           'EAD Card (Work Authorization)',
  i20_certificate:    'I-20 Certificate',
  i983_form:          'I-983 Training Plan',
  i797:               'I-797 Approval Notice',
  gc_card:            'Green Card (I-551)',
  degree_bachelors:   "Bachelor's Degree Certificate",
  degree_masters:     "Master's Degree Certificate",
  signed_offer_letter:'Signed Offer Letter',
  everify_result:     'e-Verify Result Document',
};
function docLabel(type) {
  return DOC_LABEL[type] || type.replace(/_/g, ' ');
}

// ── HR Document Verification ──────────────────────────────────────────────────
function HRDocumentVerification({ candidate, onRefresh, role }) {
  const canAct    = ['super_admin','compliance_admin','hr'].includes(role);
  const canDelete = ['super_admin','compliance_admin'].includes(role);

  const [docs,        setDocs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState({});
  const [msg,         setMsg]         = useState('');
  const [error,       setError]       = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploadFor,     setUploadFor]     = useState(null);
  const [resendFor,     setResendFor]     = useState(null); // {docId} waiting for reason input
  const [resendReason,  setResendReason]  = useState('');
  const uploadRef = React.useRef();

  const fetchDocs = useCallback(() => {
    setLoading(true);
    candidatesAPI.documentsList(candidate.id)
      .then(res => {
        const d = res.data?.data || res.data || {};
        setDocs(Array.isArray(d.documents) ? d.documents : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [candidate.id]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  async function verify(docId) {
    setSaving(p => ({ ...p, [docId]: 'verify' }));
    setMsg(''); setError('');
    try {
      await candidatesAPI.verifyDocument({
        candidate_document_id: docId,
        document_valid: 1, info_matches: 1, expiry_verified: 1, quality_verified: 1, status: 'verified',
      });
      setMsg('Document verified.');
      fetchDocs(); onRefresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to verify.');
    } finally { setSaving(p => ({ ...p, [docId]: null })); }
  }

  async function resend(docId, reason) {
    setSaving(p => ({ ...p, [docId]: 'resend' }));
    setMsg(''); setError(''); setResendFor(null); setResendReason('');
    try {
      await candidatesAPI.requestReupload({ candidate_document_id: docId, reason });
      await candidatesAPI.sendDocLink({ candidate_id: candidate.id });
      setMsg('Re-upload requested and email sent to candidate.');
      fetchDocs();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed.');
    } finally { setSaving(p => ({ ...p, [docId]: null })); }
  }

  async function deleteDoc(docId) {
    setSaving(p => ({ ...p, [docId]: 'delete' }));
    setMsg(''); setError(''); setDeleteConfirm(null);
    try {
      await candidatesAPI.deleteDocument(docId);
      setMsg('Document deleted.');
      fetchDocs(); onRefresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Delete failed.');
    } finally { setSaving(p => ({ ...p, [docId]: null })); }
  }

  async function handleAdminUpload(e) {
    const file = e.target.files?.[0];
    if (!file || !uploadFor) return;
    // Reset input so the same file can be picked again next time
    e.target.value = '';
    const allowed = ['application/pdf','image/jpeg','image/png','image/jpg'];
    if (!allowed.includes(file.type)) { setError('Allowed: PDF, JPG, PNG.'); return; }
    const currentUploadFor = { ...uploadFor }; // capture before any state resets
    setSaving(p => ({ ...p, [currentUploadFor.docId]: 'upload' }));
    setMsg(''); setError('');
    try {
      const fd = new FormData();
      fd.append('document_file', file);
      fd.append('candidate_id', candidate.id);
      fd.append('document_type', currentUploadFor.docType);
      await candidatesAPI.adminUploadDoc(fd);
      setMsg('Document uploaded by admin.');
      setUploadFor(null);
      fetchDocs(); onRefresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed.');
    } finally { setSaving(p => ({ ...p, [currentUploadFor.docId]: null })); }
  }

  if (loading) return (
    <div className="flex justify-center py-10">
      <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // Show ALL uploaded docs (never hide anything the candidate submitted),
  // then append empty placeholder slots for required types that haven't been uploaded yet.
  const requiredTypes  = VISA_REQUIRED_DOCS[candidate.visa_type] || VISA_REQUIRED_DOCS.Other;
  const uploadedTypes  = new Set(docs.map(d => d.document_type));
  const missingTypes   = requiredTypes.filter(t => !uploadedTypes.has(t));
  const fullDocList    = [
    ...docs,
    ...missingTypes.map(type => ({
      id: null, document_type: type, file_path: null, verification_status: 'not_uploaded',
    })),
  ];

  const requiredTypeSet = new Set(requiredTypes);
  const verifiedCount   = fullDocList.filter(d => d.verification_status === 'verified').length;

  // Only required docs must be verified/N-A to enable the "all verified" banner.
  // Optional docs (degree_masters, resume, etc.) never block the advance.
  const requiredDocList  = fullDocList.filter(d => requiredTypeSet.has(d.document_type));
  const hasEmptyRequired = requiredDocList.some(d => !d.id);
  const allVerified      = requiredDocList.length > 0 && !hasEmptyRequired &&
    requiredDocList.every(d => d.verification_status === 'verified' || d.verification_status === 'not_applicable');

  return (
    <div className="space-y-3">
      {/* Candidate details summary */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1.5 text-sm">
        <div><span className="text-slate-500 text-xs font-medium">Name: </span><span className="text-slate-800 font-medium">{candidate.first_name} {candidate.last_name}</span></div>
        <div><span className="text-slate-500 text-xs font-medium">Email: </span><span className="text-slate-800">{candidate.email}</span></div>
        <div><span className="text-slate-500 text-xs font-medium">Mobile: </span><span className="text-slate-800">{candidate.mobile || '—'}</span></div>
        <div><span className="text-slate-500 text-xs font-medium">Visa: </span><span className="text-slate-800 font-mono text-xs bg-slate-200 px-1.5 py-0.5 rounded">{candidate.visa_type || '—'}</span></div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800">Document Verification</h3>
          <p className="text-xs text-slate-500 mt-0.5">{verifiedCount} of {fullDocList.length} verified</p>
        </div>
        {docs.length > 0 && (
          <div className="h-2 w-32 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-2 bg-green-500 rounded-full transition-all duration-500"
              style={{ width: `${fullDocList.length ? (verifiedCount / fullDocList.length) * 100 : 0}%` }} />
          </div>
        )}
      </div>

      {msg   && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-2 text-sm">{msg}</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 text-sm">{error}</div>}

      {docs.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400 text-sm">
          No documents uploaded yet.
        </div>
      )}

      {fullDocList.map(doc => {
        const isEmpty       = !doc.id;
        const isVerified    = doc.verification_status === 'verified';
        const isReupload    = doc.verification_status === 'reupload_requested';
        const isNA          = doc.verification_status === 'not_applicable';
        const isOptional    = !requiredTypeSet.has(doc.document_type);
        const isAutoVerified = doc.document_type === 'signed_offer_letter';
        const previewUrl    = doc.file_path ? candidatesAPI.previewDocUrl(doc.id) : null;
        const busy          = saving[doc.id] || saving[doc.document_type];

        return (
          <div key={doc.id || doc.document_type} className={`bg-white rounded-xl border px-5 py-4 ${
            isVerified  ? 'border-green-200'  :
            isReupload  ? 'border-orange-200' :
            isNA        ? 'border-amber-200'  :
            isEmpty && !isOptional ? 'border-red-100 bg-red-50' :
            isEmpty     ? 'border-slate-100 bg-slate-50' :
                          'border-slate-200'
          }`}>
            <div className="flex items-start gap-4">
              {/* Status icon */}
              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm mt-0.5 ${
                isVerified              ? 'bg-green-100 text-green-600'   :
                isReupload              ? 'bg-orange-100 text-orange-500' :
                isNA                    ? 'bg-amber-100 text-amber-600'   :
                isEmpty && !isOptional  ? 'bg-red-100 text-red-400'       :
                isEmpty                 ? 'bg-slate-100 text-slate-400'   :
                                          'bg-slate-100 text-slate-400'
              }`}>
                {isVerified ? '✓' : isReupload ? '↺' : isNA ? '–' : isEmpty && !isOptional ? '!' : isEmpty ? '–' : '?'}
              </div>

              {/* Doc info */}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-slate-800 flex items-center gap-2">
                  {docLabel(doc.document_type)}
                  {isOptional && (
                    <span className="text-xs font-normal text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                      Optional
                    </span>
                  )}
                </p>
                <div className="text-xs text-slate-500 mt-0.5 space-y-0.5">
                  {isEmpty && !isOptional
                    ? <p className="text-red-500 font-medium">Not uploaded — required document missing</p>
                    : isEmpty
                    ? <p className="text-slate-400">Not uploaded — optional, not required to proceed</p>
                    : isNA
                    ? <p className="text-amber-600">Marked as not applicable</p>
                    : <>
                        <p>{doc.file_name || <span className="italic">No file</span>}</p>
                        {doc.document_number && <p>Doc #: {doc.document_number}</p>}
                        {doc.uscis_number    && <p>USCIS #: {doc.uscis_number}</p>}
                        {doc.ssn_last4      && <p>SSN Last 4: ****{doc.ssn_last4}</p>}
                        {doc.expiry_date    && <p>Expires: {new Date(doc.expiry_date).toLocaleDateString()}</p>}
                        {doc.degree_type    && <p>Degree: {doc.degree_type.replace(/_/g,' ')}</p>}
                      </>
                  }
                </div>
                {isVerified && <p className="text-xs text-green-600 mt-1">Verified{doc.verified_at ? ` on ${new Date(doc.verified_at).toLocaleDateString()}` : ''}</p>}
                {isReupload && <p className="text-xs text-orange-600 mt-1">Re-upload requested — waiting for candidate</p>}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                {/* ── Empty slot (deleted / never uploaded) ── */}
                {isEmpty && canAct && (<>
                  <button
                    onClick={() => candidatesAPI.sendDocLink({ candidate_id: candidate.id }).then(() => setMsg('Upload link sent to candidate.'))}
                    disabled={!!busy}
                    className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 px-3 py-1.5 rounded-lg disabled:opacity-50">
                    📧 Send Upload Link
                  </button>
                  <button
                    onClick={() => { setUploadFor({ docId: null, docType: doc.document_type }); uploadRef.current?.click(); }}
                    disabled={!!busy}
                    className="text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 px-3 py-1.5 rounded-lg disabled:opacity-50">
                    {busy === 'upload' ? '…' : '↑ Upload for Candidate'}
                  </button>
                </>)}

                {/* ── Normal doc slot ── */}
                {!isEmpty && (<>
                  {/* View */}
                  {previewUrl && (
                    <a href={previewUrl} target="_blank" rel="noopener noreferrer"
                      className="text-xs font-medium text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg">
                      👁 View
                    </a>
                  )}

                  {/* Verify — not shown for signed_offer_letter (auto-verified on upload) */}
                  {canAct && !isVerified && !isNA && !isAutoVerified && doc.file_path && (
                    <button onClick={() => verify(doc.id)} disabled={!!busy}
                      className="text-xs font-semibold text-white bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded-lg disabled:opacity-50">
                      {busy === 'verify' ? '…' : '✓ Verify'}
                    </button>
                  )}

                  {/* Request Re-upload — not shown for signed_offer_letter */}
                  {canAct && !isReupload && !isNA && !isAutoVerified && doc.file_path && !isVerified && (
                    <button onClick={() => { setResendFor(doc.id); setResendReason(''); }}
                      disabled={!!busy}
                      className="text-xs font-semibold text-orange-700 bg-orange-50 border border-orange-200 hover:bg-orange-100 px-3 py-1.5 rounded-lg disabled:opacity-50">
                      ↺ Request Re-upload
                    </button>
                  )}

                  {/* Upload for Candidate (reupload requested or no file but record exists) */}
                  {canAct && (isReupload || !doc.file_path) && !isNA && (
                    <button
                      onClick={() => { setUploadFor({ docId: doc.id, docType: doc.document_type }); uploadRef.current?.click(); }}
                      disabled={!!busy}
                      className="text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 px-3 py-1.5 rounded-lg disabled:opacity-50">
                      {busy === 'upload' ? '…' : '↑ Upload for Candidate'}
                    </button>
                  )}

                  {/* Re-upload (verified, admin wants to replace) */}
                  {canAct && isVerified && (
                    <button
                      onClick={() => { setUploadFor({ docId: doc.id, docType: doc.document_type }); uploadRef.current?.click(); }}
                      disabled={!!busy}
                      className="text-xs font-medium text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 px-3 py-1.5 rounded-lg disabled:opacity-50">
                      {busy === 'upload' ? '…' : '↑ Re-upload'}
                    </button>
                  )}

                  {/* Delete — only for docs that actually exist in DB */}
                  {canDelete && (
                    <button onClick={() => setDeleteConfirm(doc.id)} disabled={!!busy}
                      className="text-xs font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg disabled:opacity-50">
                      {busy === 'delete' ? '…' : '🗑 Delete'}
                    </button>
                  )}
                </>)}
              </div>
            </div>

            {/* Delete confirm inline */}
            {doc.id && deleteConfirm === doc.id && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 flex items-center gap-3">
                <p className="text-xs text-red-700 flex-1">Delete this document permanently?</p>
                <button onClick={() => deleteDoc(doc.id)} disabled={!!busy}
                  className="text-xs font-semibold bg-red-600 text-white px-3 py-1 rounded-lg hover:bg-red-700 disabled:opacity-50">
                  {busy === 'delete' ? '…' : 'Yes, Delete'}
                </button>
                <button onClick={() => setDeleteConfirm(null)} disabled={!!busy}
                  className="text-xs text-red-600 hover:text-red-800">Cancel</button>
              </div>
            )}

            {/* Re-upload reason inline */}
            {doc.id && resendFor === doc.id && (
              <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 space-y-2">
                <p className="text-xs font-semibold text-orange-800">Enter reason for re-upload request:</p>
                <textarea
                  value={resendReason}
                  onChange={e => setResendReason(e.target.value)}
                  rows={2}
                  placeholder="e.g. Document is blurry, expiry date missing, name mismatch…"
                  className="w-full text-xs border border-orange-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white resize-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => { if (resendReason.trim()) resend(doc.id, resendReason.trim()); }}
                    disabled={!resendReason.trim() || !!busy}
                    className="text-xs font-semibold bg-orange-500 text-white px-3 py-1.5 rounded-lg hover:bg-orange-600 disabled:opacity-50">
                    {busy === 'resend' ? '…' : 'Send Re-upload Request'}
                  </button>
                  <button onClick={() => { setResendFor(null); setResendReason(''); }}
                    className="text-xs text-orange-700 hover:text-orange-900">Cancel</button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Hidden file input for admin upload */}
      <input ref={uploadRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleAdminUpload} className="hidden" />

      {allVerified && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center text-green-700 text-sm font-semibold">
          ✓ All {docs.length} documents verified — ready for Admin Review
        </div>
      )}
    </div>
  );
}

// ─── Pipeline definition ──────────────────────────────────────────────────────
const PIPELINE = [
  {
    key: 'lead',
    label: 'Lead Intake',
    statuses: ['screening_pending'],
  },
  {
    key: 'screening',
    label: 'Screening',
    statuses: ['screening_in_progress', 'on_hold'],
  },
  {
    key: 'documents',
    label: 'Documents',
    statuses: ['hr_docs_pending', 'docs_submitted', 'docs_under_review'],
  },
  {
    key: 'admin_review',
    label: 'Admin Review',
    statuses: ['ready_for_admin_review', 'offer_pending', 'offer_approved'],
  },
  {
    key: 'offer',
    label: 'Offer',
    statuses: ['offer_sent', 'offer_accepted', 'offer_declined'],
  },
  {
    key: 'onboarding',
    label: 'Onboarding',
    statuses: ['day1_scheduled', 'onboarding_complete'],
  },
  {
    key: 'i9',
    label: 'I-9 & Convert',
    statuses: ['i9_pending', 'i9_employee_complete', 'i9_hr_complete', 'i9_approved', 'converted_to_employee'],
  },
];

// on_hold is intentionally NOT terminal — admin can revert it back to screening_pending
const TERMINAL = ['rejected', 'converted_to_employee'];

function getStageIndex(status) {
  for (let i = 0; i < PIPELINE.length; i++) {
    if (PIPELINE[i].statuses.includes(status)) return i;
  }
  return 0;
}

// ─── Status badge ─────────────────────────────────────────────────────────────
const STATUS_META = {
  screening_pending:      { label: 'Awaiting Screening',         color: 'bg-slate-100 text-slate-700 border-slate-200' },
  screening_in_progress:  { label: 'Screening In Progress',      color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  on_hold:                { label: 'On Hold',                    color: 'bg-gray-100 text-gray-700 border-gray-200' },
  hr_docs_pending:        { label: 'Documents Pending',          color: 'bg-orange-100 text-orange-800 border-orange-200' },
  docs_submitted:         { label: 'Documents Submitted',        color: 'bg-blue-100 text-blue-800 border-blue-200' },
  docs_under_review:      { label: 'Documents Under Review',     color: 'bg-blue-100 text-blue-800 border-blue-200' },
  ready_for_admin_review: { label: 'Ready for Admin Review',     color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  offer_pending:          { label: 'Offer Details Pending',      color: 'bg-purple-100 text-purple-800 border-purple-200' },
  offer_approved:         { label: 'Offer Authorized',           color: 'bg-violet-100 text-violet-800 border-violet-200' },
  offer_sent:             { label: 'Offer Sent',                 color: 'bg-purple-100 text-purple-800 border-purple-200' },
  offer_accepted:         { label: 'Offer Accepted',             color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  offer_declined:         { label: 'Offer Declined',             color: 'bg-red-100 text-red-800 border-red-200' },
  day1_scheduled:         { label: 'Day 1 Scheduled',            color: 'bg-teal-100 text-teal-800 border-teal-200' },
  onboarding_complete:    { label: 'Onboarding Complete',        color: 'bg-teal-100 text-teal-800 border-teal-200' },
  i9_pending:             { label: 'I-9 Link Sent',              color: 'bg-orange-100 text-orange-800 border-orange-200' },
  i9_employee_complete:   { label: 'I-9 Employee Section Done',  color: 'bg-blue-100 text-blue-800 border-blue-200' },
  i9_hr_complete:         { label: 'I-9 HR Section Done',        color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  i9_approved:            { label: 'I-9 Approved',               color: 'bg-green-100 text-green-800 border-green-200' },
  converted_to_employee:  { label: 'Converted to Employee',      color: 'bg-teal-100 text-teal-800 border-teal-200' },
  rejected:               { label: 'Rejected',                   color: 'bg-red-100 text-red-800 border-red-200' },
};

function StatusBadge({ status, size = 'md' }) {
  const meta  = STATUS_META[status] || { label: status?.replace(/_/g, ' ') || 'Unknown', color: 'bg-slate-100 text-slate-700 border-slate-200' };
  const sz    = size === 'lg' ? 'px-3 py-1.5 text-sm' : 'px-2.5 py-1 text-xs';
  return (
    <span className={`inline-flex items-center rounded-full font-medium border ${sz} ${meta.color}`}>
      {meta.label}
    </span>
  );
}

// ─── Pipeline stepper ─────────────────────────────────────────────────────────
function PipelineStepper({ status, candidateId, role }) {
  if (status === 'rejected')            return <RejectedBanner />;
  if (status === 'converted_to_employee') return <ConvertedBanner candidateId={candidateId} role={role} />;

  const current = getStageIndex(status);

  return (
    <div className="bg-white rounded-xl border border-slate-200 px-6 py-4 overflow-x-auto">
      <div className="flex items-center min-w-max">
        {PIPELINE.map((stage, i) => {
          const done    = i < current;
          const active  = i === current;
          const future  = i > current;
          return (
            <React.Fragment key={stage.key}>
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
                  done   ? 'bg-green-500 border-green-500 text-white' :
                  active ? 'bg-blue-600 border-blue-600 text-white ring-4 ring-blue-100' :
                           'bg-white border-slate-300 text-slate-400'
                }`}>
                  {done ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : i + 1}
                </div>
                <span className={`mt-1.5 text-xs font-medium whitespace-nowrap ${
                  active ? 'text-blue-600' : done ? 'text-green-600' : 'text-slate-400'
                }`}>{stage.label}</span>
              </div>
              {i < PIPELINE.length - 1 && (
                <div className={`h-0.5 w-12 mx-1 mt-[-16px] ${done ? 'bg-green-400' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function RejectedBanner() {
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-4 flex items-center gap-3">
      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
        <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <div>
        <p className="font-semibold text-red-800">Candidate Rejected</p>
        <p className="text-red-600 text-sm">This candidate did not pass screening.</p>
      </div>
    </div>
  );
}

function ConvertedBanner({ candidateId, role }) {
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState('');
  const [error, setError] = useState('');
  const isAdmin = ['super_admin', 'compliance_admin'].includes(role);

  async function resync() {
    setBusy(true); setMsg(''); setError('');
    try {
      await candidatesAPI.convertToEmployee({ candidate_id: candidateId });
      setMsg('Employee record re-synced successfully with all uploaded documents.');
    } catch (err) {
      setError(err.response?.data?.message || 'Re-sync failed.');
    } finally { setBusy(false); }
  }

  return (
    <div className="bg-teal-50 border border-teal-200 rounded-xl px-6 py-4 space-y-2">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center shrink-0">
          <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="flex-1">
          <p className="font-semibold text-teal-800">Converted to Employee</p>
          <p className="text-teal-600 text-sm">This candidate has been onboarded as an employee.</p>
        </div>
        {isAdmin && (
          <button onClick={resync} disabled={busy}
            className="text-xs font-medium text-teal-700 border border-teal-300 bg-white hover:bg-teal-50 px-3 py-1.5 rounded-lg disabled:opacity-50 shrink-0">
            {busy ? 'Syncing…' : '↺ Re-sync Employee Data'}
          </button>
        )}
      </div>
      {msg   && <p className="text-xs text-teal-700 bg-teal-100 rounded px-3 py-1.5">✓ {msg}</p>}
      {error && <p className="text-xs text-red-700 bg-red-50 rounded px-3 py-1.5">⚠ {error}</p>}
    </div>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ title, children, accent = false }) {
  return (
    <div className={`rounded-xl border p-6 space-y-4 ${accent ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-200'}`}>
      <h3 className={`font-semibold text-base ${accent ? 'text-blue-800' : 'text-slate-800'}`}>{title}</h3>
      {children}
    </div>
  );
}

function InfoGrid({ items }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
      {items.map(([label, value]) => (
        <div key={label} className="text-sm">
          <span className="text-slate-500 font-medium">{label}: </span>
          <span className="text-slate-800">{value || <span className="text-slate-400">—</span>}</span>
        </div>
      ))}
    </div>
  );
}

function Btn({ onClick, disabled, variant = 'primary', children, type = 'button' }) {
  const styles = {
    primary:   'bg-blue-600 text-white hover:bg-blue-700',
    green:     'bg-green-600 text-white hover:bg-green-700',
    teal:      'bg-teal-600 text-white hover:bg-teal-700',
    orange:    'bg-orange-500 text-white hover:bg-orange-600',
    outline:   'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50',
    danger:    'bg-red-600 text-white hover:bg-red-700',
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${styles[variant]}`}>
      {children}
    </button>
  );
}

// ─── Action card — shows exactly what needs to happen now ─────────────────────
function ActionCard({ candidate, role, onRefresh, docs, screening }) {
  const status = candidate.status;
  const id     = candidate.id;
  const [busy, setBusy]         = useState(false);
  const [msg, setMsg]           = useState('');
  const [error, setError]       = useState('');
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [i9Record, setI9Record] = useState(null);

  // Auto-fetch i9 record when candidate is in an I-9 stage
  useEffect(() => {
    const i9Statuses = ['i9_pending','i9_employee_complete','i9_hr_complete','i9_approved'];
    if (!i9Statuses.includes(status)) return;
    i9API.list({ candidate_id: id })
      .then(res => {
        const records = res.data?.data?.records || [];
        if (records.length > 0) setI9Record(records[0]);
      })
      .catch(() => {});
  }, [status, id]);

  // Screening form state (used when action is screening)
  const [sForm, setSForm] = useState(() => ({
    current_job_title: screening?.current_job_title || '',
    years_of_experience: screening?.years_of_experience || '',
    technology_skills: screening?.technology_skills || '',
    current_employer: screening?.current_employer || '',
    willing_to_relocate: !!screening?.willing_to_relocate,
    current_location: screening?.current_location || screening?.preferred_location || '',
    communication_rating: screening?.communication_rating || '',
    attitude_rating: screening?.attitude_rating || '',
    confidence_rating: screening?.confidence_rating || '',
    professionalism_rating: screening?.professionalism_rating || '',
    decision: screening?.decision || 'eligible',
    comments: screening?.comments || '',
  }));

  // Offer form state
  const [oForm, setOForm] = useState({
    stream: '', department: '', offer_date: '', joining_date: '',
    supervisor_name: '', supervisor_email: '', course_track: '', admin_notes: '',
  });

  // Step 5 — editable offer details for HR before sending
  const [sendForm, setSendForm] = useState({
    offer_date: '', joining_date: '', stream: '',
  });

  // Pre-populate sendForm from admin_review whenever candidate data loads
  useEffect(() => {
    const ar = candidate?.admin_review;
    if (!ar) return;
    const joiningDate = ar.joining_date?.slice(0, 10) || '';
    setSendForm({
      offer_date:   ar.offer_date?.slice(0, 10) || '',
      joining_date: joiningDate,
      stream:       ar.stream || '',
    });
    // Pre-fill onboarding session date with joining date
    if (joiningDate) setBForm(f => ({ ...f, session_date: joiningDate }));
  }, [candidate]);

  // Onboarding form state
  const [bForm, setBForm] = useState({
    session_date: '',
    session_time: '10:00',
    meeting_link: '',
    notes: '',
  });
  const [showReschedule, setShowReschedule] = useState(false);

  // Verify modal
  const [verifyModal, setVerifyModal] = useState(null);
  const [vChecks, setVChecks] = useState({ document_valid: false, info_matches: false, expiry_verified: false, quality_verified: false });

  // I-9 file upload state
  const [completedI9File, setCompletedI9File] = useState(null);
  const [everifyDone,     setEverifyDone]     = useState(false);
  const [everifyFile,     setEverifyFile]     = useState(null);
  const [i9Uploading,     setI9Uploading]     = useState(false);
  const completedI9Ref = React.useRef();
  const everifyRef     = React.useRef();

  function withFeedback(fn) {
    return async (...args) => {
      setBusy(true); setMsg(''); setError('');
      try {
        await fn(...args);
        onRefresh();
      } catch (err) {
        setError(err.response?.data?.message || 'Action failed. Please try again.');
      } finally { setBusy(false); }
    };
  }

  const REVERT_LABELS = {
    screening_in_progress: 'Screening Pending',    on_hold: 'Screening Pending',
    hr_docs_pending:       'Screening In Progress', docs_submitted: 'Documents Pending',
    docs_under_review:     'Documents Submitted',  ready_for_admin_review: 'Documents Submitted',
    offer_pending:         'Ready for Admin Review', offer_approved: 'Ready for Admin Review',
    offer_sent:            'Offer Authorized',      offer_accepted: 'Offer Sent',
    offer_declined:        'Offer Sent',            day1_scheduled: 'Offer Accepted',
    onboarding_complete:   'Day 1 Scheduled',       i9_pending: 'Onboarding Complete',
    i9_employee_complete:  'I-9 Link Sent',         i9_hr_complete: 'I-9 Employee Done',
    i9_approved:           'I-9 HR Done',
  };

  const doRevertStage = withFeedback(async () => {
    await candidatesAPI.revertStage({ candidate_id: id });
    setConfirmRevert(false);
    setMsg('Stage reverted successfully.');
  });

  const doSendDocLink   = withFeedback(async () => { await candidatesAPI.sendDocLink({ candidate_id: id }); setMsg('Document upload link sent to candidate.'); });
  const doSendOffer     = withFeedback(async () => { await candidatesAPI.sendOffer({ candidate_id: id, ...sendForm }); setMsg('Offer letter sent to candidate.'); });
  const doAccept        = withFeedback(async () => { await candidatesAPI.offerResponse({ candidate_id: id, response: 'accepted' }); setMsg('Offer marked as accepted.'); });
  const doDecline       = withFeedback(async () => { await candidatesAPI.offerResponse({ candidate_id: id, response: 'declined' }); setMsg('Offer marked as declined.'); });
  const doCompleteOnboard = withFeedback(async () => { await candidatesAPI.completeOnboarding({ candidate_id: id }); setMsg('Onboarding marked complete.'); });
  const doSendI9        = withFeedback(async () => { await i9API.sendLink({ candidate_id: id }); setMsg('I-9 link sent to candidate.'); });

  const doSaveScreening = withFeedback(async () => {
    await screeningAPI.save({ ...sForm, candidate_id: id });
    setMsg('Screening saved. ' + (sForm.decision === 'eligible' ? 'Candidate marked Eligible — HR can now send document link.' : sForm.decision === 'rejected' ? 'Candidate rejected.' : 'Candidate placed on hold.'));
  });

  const doAdminApprove = withFeedback(async () => {
    await candidatesAPI.adminReview({ ...oForm, candidate_id: id });
    await candidatesAPI.authorizeOffer({ candidate_id: id });
    setMsg('Offer authorized. HR can now send the offer letter.');
  });

  const doScheduleOnboard = withFeedback(async () => {
    await candidatesAPI.scheduleOnboarding({ ...bForm, candidate_id: id });
    setMsg('Day 1 onboarding session scheduled. Invitation email sent.');
  });

  const doReschedule = withFeedback(async () => {
    await candidatesAPI.rescheduleOnboarding({ ...bForm, candidate_id: id });
    setShowReschedule(false);
    setMsg('Session rescheduled and updated invitation sent to candidate.');
  });

  const doResendInvite = withFeedback(async () => {
    await candidatesAPI.resendOnboardingInvite({ candidate_id: id });
    setMsg('Invitation resent to candidate.');
  });

  const doVerifyDoc = withFeedback(async () => {
    await candidatesAPI.verifyDocument({ candidate_document_id: verifyModal.id, ...vChecks, status: 'verified' });
    setVerifyModal(null);
    setMsg('Document verified.');
  });

  async function doUploadCompletedI9() {
    if (!completedI9File || !i9Record?.id) return;
    setI9Uploading(true); setMsg(''); setError('');
    try {
      const fd = new FormData();
      fd.append('i9_id', i9Record.id);
      fd.append('completed_i9_file', completedI9File);
      await i9API.uploadCompleted(fd);
      setCompletedI9File(null);
      setMsg('Completed I-9 uploaded. Candidate status updated.');
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed.');
    } finally { setI9Uploading(false); }
  }

  async function doApproveI9() {
    if (!i9Record?.id) return;
    if (!everifyDone) { setError('Please confirm e-Verify is completed.'); return; }
    if (!everifyFile && !i9Record?.everify_file_path) {
      setError('Please upload the e-Verify result document before approving.');
      return;
    }
    setBusy(true); setMsg(''); setError('');
    try {
      const fd = new FormData();
      fd.append('i9_id', i9Record.id);
      fd.append('everify_done', 1);
      if (everifyFile) fd.append('everify_file', everifyFile);
      await i9API.adminApprove(fd);
      setMsg('I-9 approved successfully.');
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Approval failed.');
    } finally { setBusy(false); }
  }

  const inputCls = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white';

  // ── Determine what to show based on status + role ──────────────────────────
  const isAdmin = ['super_admin', 'compliance_admin'].includes(role);
  const isHR    = ['super_admin', 'compliance_admin', 'hr'].includes(role);
  const isScreening = ['super_admin', 'compliance_admin', 'screening'].includes(role);

  let title = '', description = '', body = null;

  if (status === 'screening_pending' || status === 'screening_in_progress') {
    title = 'Step 1 of 7 — Screening Assessment';
    description = 'Complete the professional, financial, and behavioral assessment for this candidate, then select a decision.';
    if (isScreening) {
      body = (
        <div className="space-y-4">
          {/* Professional */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Previous Job Title (before joining VSP Astra)</label>
              <input value={sForm.current_job_title} onChange={e=>setSForm(f=>({...f,current_job_title:e.target.value}))} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Years of Experience</label>
              <input value={sForm.years_of_experience} onChange={e=>setSForm(f=>({...f,years_of_experience:e.target.value}))} className={inputCls} placeholder="e.g. 3" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Previous Employer (before joining VSP Astra)</label>
              <input value={sForm.current_employer} onChange={e=>setSForm(f=>({...f,current_employer:e.target.value}))} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Current Location</label>
              <input value={sForm.current_location} onChange={e=>setSForm(f=>({...f,current_location:e.target.value}))} className={inputCls} placeholder="City, State" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Technology Skills</label>
            <textarea value={sForm.technology_skills} onChange={e=>setSForm(f=>({...f,technology_skills:e.target.value}))} rows={2} className={`${inputCls} resize-none`} />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={sForm.willing_to_relocate} onChange={e=>setSForm(f=>({...f,willing_to_relocate:e.target.checked}))} className="rounded" />
            Willing to Relocate
          </label>
          <div className="grid grid-cols-4 gap-3">
            {[['communication_rating','Communication'],['attitude_rating','Attitude'],['confidence_rating','Confidence'],['professionalism_rating','Professionalism']].map(([k,l])=>(
              <div key={k}>
                <label className="block text-xs font-medium text-slate-600 mb-1">{l} (1–5)</label>
                <input type="number" min={1} max={5} value={sForm[k]} onChange={e=>setSForm(f=>({...f,[k]:e.target.value}))} className={inputCls} placeholder="1–5" />
              </div>
            ))}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Decision</label>
            <div className="flex gap-4">
              {[['eligible','✓ Eligible','text-green-700 bg-green-50 border-green-300'],['hold','⏸ On Hold','text-yellow-700 bg-yellow-50 border-yellow-300'],['rejected','✗ Reject','text-red-700 bg-red-50 border-red-300']].map(([v,l,cls])=>(
                <label key={v} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm font-medium ${sForm.decision===v ? cls : 'border-slate-200 text-slate-500 bg-white'}`}>
                  <input type="radio" name="decision" value={v} checked={sForm.decision===v} onChange={()=>setSForm(f=>({...f,decision:v}))} className="sr-only" />
                  {l}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Screening Comments</label>
            <textarea value={sForm.comments} onChange={e=>setSForm(f=>({...f,comments:e.target.value}))} rows={2} className={`${inputCls} resize-none`} />
          </div>
          <Btn onClick={() => { doSaveScreening(); }} disabled={busy} variant="primary">
            {busy ? 'Saving...' : 'Save Screening & Submit Decision'}
          </Btn>
        </div>
      );
    } else {
      body = <p className="text-sm text-slate-500">Waiting for screening team to complete assessment.</p>;
    }
  }

  else if (status === 'on_hold') {
    title = 'Candidate On Hold';
    description = 'This candidate was placed on hold during screening. Use the revert option below to return them to screening, or leave on hold.';
    body = (
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
        <p className="font-medium mb-1">On Hold — pending decision</p>
        <p className="text-amber-700 text-xs">No action required right now. Use <strong>"Go back to: Screening Pending"</strong> below to restart the screening process for this candidate.</p>
      </div>
    );
  }

  else if (status === 'hr_docs_pending') {
    title = 'Step 2 of 7 — Request Documents from Candidate';
    description = 'Send a secure document upload link to the candidate. They will upload all required documents without needing to log in.';
    if (isHR) {
      body = (
        <div className="space-y-3">
          <div className="bg-white rounded-lg border border-blue-200 p-4 text-sm text-slate-600">
            <p className="font-medium text-slate-800 mb-1">Required for {candidate.visa_type}:</p>
            <p>Passport · Visa Copy · I-94 · EAD Card · I-20 · SSN · Resume · Degree Certificate</p>
          </div>
          <Btn onClick={doSendDocLink} disabled={busy} variant="primary">
            {busy ? 'Sending...' : '📧 Send Document Upload Link to Candidate'}
          </Btn>
        </div>
      );
    } else {
      body = <p className="text-sm text-slate-500">Waiting for HR to send the document upload link.</p>;
    }
  }

  else if (status === 'docs_submitted' || status === 'docs_under_review') {
    title = 'Step 3 of 7 — Document Verification';
    description = 'Review each document uploaded by the candidate. See details, preview the file, approve or reject individually, then submit.';
    body = null; // Rendered as HRDocumentVerification below the action card
  }

  else if (status === 'ready_for_admin_review') {
    title = 'Step 4 of 7 — Admin Review & Offer Authorization';
    description = 'Review screening results and documents, then fill in the offer details and authorize the offer letter.';
    if (isAdmin) {
      body = (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Stream <span className="text-red-400">*</span></label>
              <input value={oForm.stream} onChange={e=>setOForm(f=>({...f,stream:e.target.value}))} className={inputCls} placeholder="e.g. Java Backend" />
            </div>
            {[['department','Department'],['course_track','Course Track'],['supervisor_name','Supervisor Name'],['supervisor_email','Supervisor Email']].map(([k,l])=>(
              <div key={k}>
                <label className="block text-xs font-medium text-slate-600 mb-1">{l}</label>
                <input value={oForm[k]} onChange={e=>setOForm(f=>({...f,[k]:e.target.value}))} className={inputCls} />
              </div>
            ))}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Offer Date <span className="text-red-400">*</span></label>
              <input type="date" value={oForm.offer_date} onChange={e=>setOForm(f=>({...f,offer_date:e.target.value}))} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Joining Date <span className="text-red-400">*</span></label>
              <input type="date" value={oForm.joining_date} onChange={e=>setOForm(f=>({...f,joining_date:e.target.value}))} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Admin Notes</label>
            <textarea value={oForm.admin_notes} onChange={e=>setOForm(f=>({...f,admin_notes:e.target.value}))} rows={2} className={`${inputCls} resize-none`} />
          </div>
          <Btn onClick={() => {
            if (!oForm.stream.trim()) { setError('Stream is required.'); return; }
            if (!oForm.offer_date)    { setError('Offer date is required.'); return; }
            if (!oForm.joining_date)  { setError('Joining date is required.'); return; }
            doAdminApprove();
          }} disabled={busy} variant="green">
            {busy ? 'Processing...' : '✓ Approve & Authorize Offer Letter'}
          </Btn>
        </div>
      );
    } else {
      body = <p className="text-sm text-slate-500">Waiting for Admin to review and authorize the offer letter.</p>;
    }
  }

  else if (status === 'offer_pending' || status === 'offer_approved') {
    title = 'Step 5 of 7 — Send Offer Letter';
    description = 'Review and edit the offer details below if needed, then send. A signed-offer upload link is automatically included in the email.';
    if (isHR) {
      body = (
        <div className="space-y-4">
          {/* Authorized badge */}
          <div className="flex items-center gap-2 text-sm">
            <span className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
            <span className="text-slate-600 font-medium">Offer authorized by admin — review details before sending</span>
          </div>

          {/* Editable offer details */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Offer Date <span className="text-red-400">*</span></label>
              <input type="date" value={sendForm.offer_date}
                onChange={e => setSendForm(f => ({ ...f, offer_date: e.target.value }))}
                className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Joining Date <span className="text-red-400">*</span></label>
              <input type="date" value={sendForm.joining_date}
                onChange={e => setSendForm(f => ({ ...f, joining_date: e.target.value }))}
                className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Job Title</label>
              <input value={sendForm.stream}
                onChange={e => setSendForm(f => ({ ...f, stream: e.target.value }))}
                className={inputCls} placeholder="e.g. Java Backend Developer" />
            </div>
          </div>

          <div className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
            📎 A signed offer upload link will be automatically included in the email.
          </div>

          <Btn onClick={() => {
            if (!sendForm.offer_date)   { setError('Offer date is required.'); return; }
            if (!sendForm.joining_date) { setError('Joining date is required.'); return; }
            doSendOffer();
          }} disabled={busy} variant="primary">
            {busy ? 'Sending...' : '📧 Send Offer Letter to Candidate'}
          </Btn>
        </div>
      );
    } else {
      body = <p className="text-sm text-slate-500">Waiting for HR to send the offer letter.</p>;
    }
  }

  else if (status === 'offer_sent') {
    title = 'Step 5 of 7 — Offer Sent — Awaiting Signed Copy';
    description = 'Edit offer details if needed and resend. Once the candidate uploads their signed copy, click Candidate Accepted.';
    if (isHR) {
      const signedOfferDoc = docs.find(d => d.document_type === 'signed_offer_letter');
      body = (
        <div className="space-y-4">

          {/* Step indicators */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <span className="text-green-600 font-bold">✓</span>
              <span className="text-green-700 font-medium">Offer email sent</span>
            </div>
            <div className={`flex items-center gap-1.5 rounded-lg px-3 py-2 border ${
              signedOfferDoc ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
            }`}>
              <span className={signedOfferDoc ? 'text-green-600 font-bold' : 'text-amber-500'}>
                {signedOfferDoc ? '✓' : '⏳'}
              </span>
              <span className={`font-medium ${signedOfferDoc ? 'text-green-700' : 'text-amber-700'}`}>
                {signedOfferDoc ? 'Signed copy received' : 'Waiting for signed copy'}
              </span>
            </div>
            <div className={`flex items-center gap-1.5 rounded-lg px-3 py-2 border ${
              signedOfferDoc ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'
            }`}>
              <span className={signedOfferDoc ? 'text-blue-600' : 'text-slate-400'}>○</span>
              <span className={`font-medium ${signedOfferDoc ? 'text-blue-700' : 'text-slate-400'}`}>
                Approve &amp; accept
              </span>
            </div>
          </div>

          {/* Signed offer doc status */}
          {signedOfferDoc ? (
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
              <div className="w-9 h-9 bg-green-500 rounded-full flex items-center justify-center shrink-0 text-white text-sm font-bold">✓</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-green-800">Signed offer letter received</p>
                <p className="text-xs text-green-600 mt-0.5 truncate">
                  {signedOfferDoc.file_name}
                  {signedOfferDoc.created_at && ` — uploaded ${new Date(signedOfferDoc.created_at).toLocaleDateString()}`}
                </p>
              </div>
              <a href={candidatesAPI.previewDocUrl(signedOfferDoc.id)} target="_blank" rel="noopener noreferrer"
                className="text-xs font-semibold text-blue-600 border border-blue-200 bg-white hover:bg-blue-50 px-3 py-1.5 rounded-lg shrink-0">
                👁 View Signed Copy
              </a>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3">
              <span className="text-amber-500 text-base shrink-0">⏳</span>
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">Waiting for candidate to upload signed offer letter</p>
                <p className="text-xs text-amber-600 mt-0.5">Use Resend below if the candidate hasn't received it or if any details changed.</p>
              </div>
            </div>
          )}

          {/* Always-visible editable offer details for resend */}
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Offer Details — edit if needed before resending</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Offer Date</label>
                <input type="date" value={sendForm.offer_date}
                  onChange={e => setSendForm(f => ({ ...f, offer_date: e.target.value }))}
                  className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Joining Date</label>
                <input type="date" value={sendForm.joining_date}
                  onChange={e => setSendForm(f => ({ ...f, joining_date: e.target.value }))}
                  className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Job Title</label>
                <input value={sendForm.stream}
                  onChange={e => setSendForm(f => ({ ...f, stream: e.target.value }))}
                  className={inputCls} placeholder="e.g. Java Backend Developer" />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {signedOfferDoc && (
              <Btn onClick={doAccept} disabled={busy} variant="green">
                {busy ? 'Processing…' : '✓ Candidate Accepted — Proceed'}
              </Btn>
            )}
            <Btn onClick={doSendOffer} disabled={busy} variant="primary">
              {busy ? 'Sending…' : '↺ Resend Offer Letter'}
            </Btn>
            <Btn onClick={doDecline} disabled={busy} variant="danger">✗ Candidate Declined</Btn>
            {!signedOfferDoc && (
              <Btn onClick={doAccept} disabled={busy} variant="outline">
                Skip — Mark Accepted Manually
              </Btn>
            )}
          </div>

        </div>
      );
    } else {
      body = <p className="text-sm text-slate-500">Offer sent — waiting for the candidate to upload their signed copy.</p>;
    }
  }

  else if (status === 'offer_declined') {
    title = 'Offer Declined';
    description = 'The candidate declined the offer. You can revert to resend it with updated details, or close out this candidate.';
    if (isHR) {
      body = (
        <div className="space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800">
            <p className="font-medium mb-1">Candidate declined the offer</p>
            <p className="text-red-700 text-xs">To resend the offer with updated details, use <strong>"Go back to: Offer Sent"</strong> below. This will restore the offer to the sent state so you can resend it.</p>
          </div>
          <div className="flex gap-2">
            <Btn onClick={doSendOffer} disabled={busy} variant="primary">
              {busy ? 'Sending…' : '↺ Resend Offer Directly'}
            </Btn>
          </div>
        </div>
      );
    } else {
      body = <p className="text-sm text-slate-500">The candidate declined the offer. Admin or HR can resend or revert.</p>;
    }
  }

  else if (status === 'offer_accepted') {
    title = 'Step 6 of 7 — Schedule Day 1 Onboarding';
    description = 'Offer accepted! Schedule the Day 1 onboarding session and send the invitation.';
    if (isHR) {
      body = (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
              <input type="date" value={bForm.session_date} onChange={e=>setBForm(f=>({...f,session_date:e.target.value}))} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Time <span className="text-slate-400 font-normal">(EST)</span></label>
              <input type="time" value={bForm.session_time} onChange={e=>setBForm(f=>({...f,session_time:e.target.value}))} className={inputCls} />
            </div>
            <div className="col-span-2">
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 text-xs text-blue-700">
                <span className="text-base">🔗</span>
                <span><strong>Microsoft Teams link will be auto-generated</strong> by our scheduling automation and included in the invitation email.</span>
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Notes for Candidate</label>
              <textarea value={bForm.notes} onChange={e=>setBForm(f=>({...f,notes:e.target.value}))} rows={2} className={`${inputCls} resize-none`} placeholder="Please bring original documents..." />
            </div>
          </div>
          <Btn onClick={doScheduleOnboard} disabled={busy} variant="teal">
            {busy ? 'Creating meeting & sending invite...' : '📅 Schedule & Send Invitation'}
          </Btn>
        </div>
      );
    }
  }

  else if (status === 'day1_scheduled') {
    title = 'Step 6 of 7 — Day 1 Onboarding Scheduled';
    description = 'Onboarding session is scheduled. Mark complete once the session is done.';
    if (isHR) {
      body = (
        <div className="space-y-3">
          {!showReschedule ? (
            <>
              <div className="flex flex-wrap gap-2">
                <Btn onClick={doCompleteOnboard} disabled={busy} variant="teal">
                  {busy ? '...' : '✓ Mark Onboarding Complete'}
                </Btn>
                <Btn onClick={() => { setShowReschedule(true); setBForm(f => ({ ...f, session_date: '', session_time: '10:00', notes: '' })); }} disabled={busy} variant="orange">
                  📅 Reschedule
                </Btn>
                <Btn onClick={doResendInvite} disabled={busy} variant="outline">
                  {busy ? '...' : '📧 Resend Invitation'}
                </Btn>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-700">Pick a new date & time — a new Teams meeting will be created and a fresh invite sent.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">New Date</label>
                  <input type="date" value={bForm.session_date} onChange={e => setBForm(f => ({ ...f, session_date: e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">New Time <span className="text-slate-400 font-normal">(EST)</span></label>
                  <input type="time" value={bForm.session_time} onChange={e => setBForm(f => ({ ...f, session_time: e.target.value }))} className={inputCls} />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Notes for Candidate</label>
                  <textarea value={bForm.notes} onChange={e => setBForm(f => ({ ...f, notes: e.target.value }))} rows={2} className={`${inputCls} resize-none`} placeholder="Please bring original documents..." />
                </div>
              </div>
              <div className="flex gap-2">
                <Btn onClick={doReschedule} disabled={busy || !bForm.session_date} variant="orange">
                  {busy ? 'Rescheduling...' : '📅 Confirm Reschedule & Send Invite'}
                </Btn>
                <Btn onClick={() => setShowReschedule(false)} disabled={busy} variant="outline">Cancel</Btn>
              </div>
            </div>
          )}
        </div>
      );
    }
  }

  else if (status === 'onboarding_complete') {
    title = 'Step 7 of 7 — I-9 Verification';
    description = 'Onboarding done. Send the I-9 completion link to the candidate for Section 1.';
    if (isHR) {
      body = <Btn onClick={doSendI9} disabled={busy} variant="primary">{busy ? 'Sending...' : '📋 Send I-9 Link to Candidate'}</Btn>;
    }
  }

  else if (status === 'i9_pending') {
    title = 'Step 7 of 7 — I-9: Waiting for Candidate Upload';
    description = 'The I-9 link has been sent. The candidate needs to upload their completed I-9 Section 1 document.';
    if (isHR) {
      body = (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
            Waiting for the candidate to upload their I-9 document. You can resend the link if needed.
          </div>
          <Btn onClick={doSendI9} disabled={busy} variant="outline">
            {busy ? 'Sending...' : '↺ Resend I-9 Link'}
          </Btn>
        </div>
      );
    } else {
      body = <p className="text-sm text-slate-500">Waiting for candidate to upload their I-9 document.</p>;
    }
  }

  else if (status === 'i9_employee_complete') {
    title = 'Step 7 of 7 — I-9: Upload Completed Form';
    description = 'Candidate has uploaded their I-9. Download it, fill in the employer section (Section 2) offline, then upload the completed form here.';
    if (isHR) {
      body = i9Record ? (
        <div className="space-y-4">
          {/* Candidate's uploaded I-9 */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-0.5">Candidate's I-9 Document</p>
              <p className="text-sm text-slate-700 truncate">{i9Record.candidate_i9_file_name || 'I-9 Document'}</p>
            </div>
            <a href={i9API.previewUrl(i9Record.id, 'candidate')} target="_blank" rel="noopener noreferrer"
              className="text-xs font-semibold text-blue-600 border border-blue-200 bg-white hover:bg-blue-50 px-3 py-1.5 rounded-lg shrink-0">
              👁 View / Download
            </a>
          </div>

          {/* Admin uploads completed I-9 */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Upload Completed I-9 (both sections filled)</p>
            {completedI9File ? (
              <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <span className="text-blue-600 text-sm">📄</span>
                <span className="text-xs text-blue-700 flex-1 truncate font-medium">{completedI9File.name}</span>
                <button onClick={() => { setCompletedI9File(null); completedI9Ref.current.value = ''; }}
                  className="text-xs text-slate-500 hover:text-red-600 border border-slate-200 bg-white px-2 py-1 rounded">
                  Remove
                </button>
              </div>
            ) : (
              <button onClick={() => completedI9Ref.current?.click()}
                className="text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 px-3 py-2 rounded-lg">
                ↑ Select Completed I-9 File
              </button>
            )}
            <input ref={completedI9Ref} type="file" accept=".pdf,.jpg,.jpeg,.png"
              onChange={e => { const f = e.target.files?.[0]; if (f) setCompletedI9File(f); }}
              className="hidden" />
          </div>

          <Btn onClick={doUploadCompletedI9} disabled={!completedI9File || i9Uploading} variant="primary">
            {i9Uploading ? 'Uploading…' : '↑ Upload Completed I-9'}
          </Btn>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          Loading I-9 details…
        </div>
      );
    } else {
      body = <p className="text-sm text-slate-500">Waiting for HR to upload the completed I-9 form.</p>;
    }
  }

  else if (status === 'i9_hr_complete') {
    title = 'Step 7 of 7 — I-9: e-Verify & Final Approval';
    description = 'Completed I-9 uploaded. Run e-Verify on the candidate, upload the result, then approve.';
    if (isAdmin) {
      body = i9Record ? (
        <div className="space-y-4">
          {/* Completed I-9 */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-0.5">Completed I-9</p>
              <p className="text-sm text-slate-700 truncate">{i9Record.completed_i9_file_name || 'Completed I-9'}</p>
            </div>
            <a href={i9API.previewUrl(i9Record.id, 'completed')} target="_blank" rel="noopener noreferrer"
              className="text-xs font-semibold text-blue-600 border border-blue-200 bg-white hover:bg-blue-50 px-3 py-1.5 rounded-lg shrink-0">
              👁 View
            </a>
          </div>

          {/* e-Verify */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={everifyDone}
                onChange={e => setEverifyDone(e.target.checked)}
                className="rounded accent-blue-600 w-4 h-4" />
              <span className="text-sm font-medium text-slate-700">
                e-Verify completed <span className="text-red-500">*</span>
              </span>
            </label>

            <div>
              <p className="text-xs font-medium text-slate-600 mb-1">
                Upload e-Verify Result
                {everifyDone
                  ? <span className="text-red-500 ml-1">* Required</span>
                  : <span className="text-slate-400 ml-1">(required when checkbox is checked)</span>
                }
              </p>
              {(i9Record.everify_file_path || everifyFile) ? (
                <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <span className="text-green-600 text-sm">✓</span>
                  <span className="text-xs text-green-700 font-medium flex-1 truncate">
                    {everifyFile ? everifyFile.name : i9Record.everify_file_name}
                  </span>
                  {i9Record.everify_file_path && !everifyFile && (
                    <a href={i9API.previewUrl(i9Record.id, 'everify')} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-blue-600 border border-blue-200 bg-white px-2 py-1 rounded hover:bg-blue-50 shrink-0">
                      👁 View
                    </a>
                  )}
                  <button onClick={() => everifyRef.current?.click()}
                    className="text-xs text-indigo-600 border border-indigo-200 bg-white px-2 py-1 rounded hover:bg-indigo-50">
                    ↑ Replace
                  </button>
                </div>
              ) : (
                <button onClick={() => everifyRef.current?.click()}
                  className={`text-xs font-semibold px-3 py-2 rounded-lg border ${
                    everifyDone
                      ? 'text-red-700 bg-red-50 border-red-300 hover:bg-red-100'
                      : 'text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100'
                  }`}>
                  ↑ Upload e-Verify Result{everifyDone ? ' *' : ''}
                </button>
              )}
              <input ref={everifyRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => { const f = e.target.files?.[0]; if (f) setEverifyFile(f); }}
                className="hidden" />
            </div>
          </div>

          <Btn
            onClick={doApproveI9}
            disabled={busy || !everifyDone || (!everifyFile && !i9Record?.everify_file_path)}
            variant="green"
          >
            {busy ? 'Approving...' : '✓ Approve I-9'}
          </Btn>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          Loading I-9 details…
        </div>
      );
    } else {
      body = <p className="text-sm text-slate-500">Waiting for admin to complete e-Verify and approve.</p>;
    }
  }

  else if (status === 'i9_approved') {
    title = 'I-9 Approved — Convert to Employee';
    description = 'All steps complete. Convert this candidate to an active employee record.';
    if (isAdmin) {
      body = (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            This will create an employee record from the candidate's data and I-9 information,
            and move them out of the candidate pipeline.
          </p>
          <Btn onClick={withFeedback(async () => {
            await candidatesAPI.convertToEmployee({ candidate_id: id });
            setMsg('Candidate successfully converted to employee.');
          })} disabled={busy} variant="teal">
            {busy ? 'Converting...' : '🎉 Convert to Employee'}
          </Btn>
        </div>
      );
    }
  }

  if (!body && !title) return null;

  return (
    <>
      <div className="bg-blue-600 rounded-xl p-5 text-white space-y-3">
        <div>
          <p className="text-blue-200 text-xs font-semibold uppercase tracking-wide mb-1">Next Action Required</p>
          <p className="font-bold text-lg leading-tight">{title}</p>
          <p className="text-blue-100 text-sm mt-1">{description}</p>
        </div>
        {msg   && <div className="bg-white/20 text-white rounded-lg px-3 py-2 text-sm">✓ {msg}</div>}
        {error && <div className="bg-red-500/80 text-white rounded-lg px-3 py-2 text-sm">⚠ {error}</div>}
        {body && <div className="bg-white rounded-lg p-4 text-slate-800">{body}</div>}

        {/* Revert stage — admin only, shown when a previous stage exists */}
        {isAdmin && REVERT_LABELS[status] && (
          <div className="border-t border-blue-500 pt-3 mt-1">
            {!confirmRevert ? (
              <button onClick={() => setConfirmRevert(true)}
                className="text-xs text-blue-200 hover:text-white underline underline-offset-2">
                ← Go back to: {REVERT_LABELS[status]}
              </button>
            ) : (
              <div className="bg-white/10 rounded-lg px-3 py-2 flex items-center gap-3">
                <p className="text-xs text-white flex-1">Revert to <strong>{REVERT_LABELS[status]}</strong>?</p>
                <button onClick={doRevertStage} disabled={busy}
                  className="text-xs font-semibold bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg disabled:opacity-50">
                  {busy ? '...' : 'Yes, Revert'}
                </button>
                <button onClick={() => setConfirmRevert(false)} disabled={busy}
                  className="text-xs text-blue-200 hover:text-white">
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Verify modal */}
      {verifyModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <h3 className="font-semibold text-slate-800 mb-1">Verify Document</h3>
            <p className="text-sm text-slate-500 mb-4 capitalize">{(verifyModal.document_type || '').replace(/_/g, ' ')}</p>
            <div className="space-y-3 mb-5">
              {[['document_valid','Document is valid and authentic'],['info_matches','Name / info matches candidate data'],['expiry_verified','Expiry date is verified'],['quality_verified','Document is legible and clear']].map(([k,l])=>(
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={vChecks[k]} onChange={e=>setVChecks(c=>({...c,[k]:e.target.checked}))} className="rounded accent-blue-600" />
                  {l}
                </label>
              ))}
            </div>
            {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
            <div className="flex gap-3">
              <Btn onClick={doVerifyDoc} disabled={busy || !Object.values(vChecks).every(Boolean)} variant="green">
                {busy ? 'Saving...' : 'Mark Verified'}
              </Btn>
              <Btn onClick={() => setVerifyModal(null)} variant="outline">Cancel</Btn>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Candidate info card ──────────────────────────────────────────────────────
function CandidateInfoCard({ candidate }) {
  return (
    <Section title="New Bench Information">
      <InfoGrid items={[
        ['Full Name',    `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim()],
        ['Email',        candidate.email],
        ['Mobile',       candidate.mobile],
        ['Visa Type',    candidate.visa_type],
        ['Location',     candidate.current_location],
        ['Referred By',  candidate.referred_by],
        ['Added On',     candidate.created_at ? new Date(candidate.created_at).toLocaleDateString('en-US', { dateStyle: 'medium' }) : null],
      ]} />
      {candidate.notes && (
        <div className="pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-500 font-medium mb-1">Notes</p>
          <p className="text-sm text-slate-700">{candidate.notes}</p>
        </div>
      )}
    </Section>
  );
}

// ─── Screening summary (read-only, shown after screening is done) ─────────────
function ScreeningSummary({ screening }) {
  if (!screening || !screening.decision) return null;
  const decisionColor = screening.decision === 'eligible' ? 'text-green-700' : screening.decision === 'rejected' ? 'text-red-700' : 'text-yellow-700';
  return (
    <Section title="Screening Results">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm text-slate-500">Decision:</span>
        <span className={`font-semibold text-sm capitalize ${decisionColor}`}>{screening.decision}</span>
      </div>
      <InfoGrid items={[
        ['Prev. Job Title',  screening.current_job_title],
        ['Experience',       screening.years_of_experience ? `${screening.years_of_experience} years` : null],
        ['Prev. Employer',   screening.current_employer],
        ['Location',        screening.current_location || screening.preferred_location],
        ['Communication',   screening.communication_rating ? `${screening.communication_rating}/5` : null],
        ['Attitude',        screening.attitude_rating ? `${screening.attitude_rating}/5` : null],
        ['Confidence',      screening.confidence_rating ? `${screening.confidence_rating}/5` : null],
        ['Professionalism', screening.professionalism_rating ? `${screening.professionalism_rating}/5` : null],
      ]} />
      {screening.comments && (
        <div className="pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-500 font-medium mb-1">Comments</p>
          <p className="text-sm text-slate-700">{screening.comments}</p>
        </div>
      )}
    </Section>
  );
}

// ─── Documents summary ────────────────────────────────────────────────────────
function DocumentsSummary({ docs }) {
  if (!docs || docs.length === 0) return null;
  const verified = docs.filter(d => d.verification_status === 'verified').length;
  return (
    <Section title={`Documents — ${verified}/${docs.length} Verified`}>
      <div className="space-y-1.5">
        {docs.map(d => (
          <div key={d.id} className="flex items-center justify-between text-sm">
            <span className="capitalize text-slate-700">{(d.document_type||'').replace(/_/g,' ')}</span>
            {d.verification_status === 'verified'
              ? <span className="text-green-600 font-medium text-xs">✓ Verified</span>
              : d.verification_status === 'reupload_requested'
              ? <span className="text-orange-500 text-xs">Re-upload Requested</span>
              : d.file_path
              ? <span className="text-blue-500 text-xs">Uploaded — Pending Review</span>
              : <span className="text-slate-400 text-xs">Not Uploaded</span>
            }
          </div>
        ))}
      </div>
    </Section>
  );
}

// ─── Activity timeline ────────────────────────────────────────────────────────
function ActivityTimeline({ history }) {
  if (!history || history.length === 0) return null;
  return (
    <Section title="Activity Timeline">
      <div className="space-y-3">
        {[...history].reverse().map((h, i) => (
          <div key={i} className="flex gap-3 text-sm">
            <div className="flex flex-col items-center">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500 mt-0.5 shrink-0" />
              {i < history.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 mt-1" />}
            </div>
            <div className="pb-3">
              <p className="font-medium text-slate-800 capitalize">{(h.new_status||'').replace(/_/g,' ')}</p>
              {h.notes && <p className="text-slate-500 text-xs mt-0.5">{h.notes}</p>}
              <p className="text-slate-400 text-xs mt-0.5">{h.created_at ? new Date(h.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : ''}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function CandidateProfile() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const { admin }  = useAuth();
  const role       = admin?.role || 'viewer';

  const [candidate,  setCandidate]  = useState(null);
  const [screening,  setScreening]  = useState(null);
  const [docs,       setDocs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  const canSeeScreening = ['super_admin','compliance_admin','screening'].includes(role);

  const fetchAll = useCallback(async () => {
    if (!id || id === 'undefined') { setError('Invalid candidate.'); setLoading(false); return; }
    try {
      const promises = [
        candidatesAPI.get(id),
        canSeeScreening ? screeningAPI.get(id) : Promise.resolve(null),
        candidatesAPI.documentsList(id),
      ];
      const [cRes, sRes, dRes] = await Promise.allSettled(promises);

      if (cRes.status === 'fulfilled') {
        const d = cRes.value.data?.data?.candidate || cRes.value.data?.data || cRes.value.data?.candidate || cRes.value.data;
        setCandidate(d);
      } else {
        setError('Failed to load candidate.');
      }

      if (sRes.status === 'fulfilled') {
        const d = sRes.value.data?.data || sRes.value.data;
        setScreening(d);
      }

      if (dRes.status === 'fulfilled') {
        const d = dRes.value.data?.data || dRes.value.data || {};
        setDocs(Array.isArray(d.documents) ? d.documents : []);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !candidate) return (
    <div className="space-y-4">
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error || 'Candidate not found.'}</div>
      <button onClick={() => navigate('/candidates')} className="text-blue-600 hover:underline text-sm">← Back to Candidates</button>
    </div>
  );

  const stageIdx  = getStageIndex(candidate.status);
  const isTerminal = TERMINAL.includes(candidate.status);

  return (
    <div className="space-y-5 max-w-4xl">

      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate('/candidates')} className="text-slate-400 hover:text-slate-700 mt-1 shrink-0" title="Back to New Bench">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-slate-900">{candidate.first_name} {candidate.last_name}</h1>
            <StatusBadge status={candidate.status} size="lg" />
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-slate-500 flex-wrap">
            <span>{candidate.email}</span>
            {candidate.mobile && <><span>·</span><span>{candidate.mobile}</span></>}
            {candidate.visa_type && <><span>·</span><span className="font-mono text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{candidate.visa_type}</span></>}
          </div>
        </div>
        <div className="text-xs text-slate-400 shrink-0 hidden sm:block">
          Logged in as <span className="font-medium text-slate-600 capitalize">{role.replace(/_/g, ' ')}</span>
        </div>
      </div>

      {/* Pipeline stepper */}
      <PipelineStepper status={candidate.status} candidateId={candidate.id} role={role} />

      {/* Action card — shown only when there's something to do */}
      {!isTerminal && (
        <ActionCard
          candidate={candidate}
          role={role}
          onRefresh={fetchAll}
          docs={docs}
          screening={screening}
        />
      )}

      {/* HR Document Verification — full-width when in docs stage */}
      {(candidate.status === 'hr_docs_pending' || candidate.status === 'docs_submitted' || candidate.status === 'docs_under_review') && (
        <HRDocumentVerification
          candidate={candidate}
          onRefresh={fetchAll}
          role={role}
        />
      )}

      {/* Info grid — 2 columns on larger screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <CandidateInfoCard candidate={candidate} />
        {screening?.decision && <ScreeningSummary screening={screening} />}
        {docs.length > 0
          && candidate.status !== 'hr_docs_pending'
          && candidate.status !== 'docs_submitted'
          && candidate.status !== 'docs_under_review'
          && <DocumentsSummary docs={docs} />}
        {candidate.status_history?.length > 0 && <ActivityTimeline history={candidate.status_history} />}
      </div>

    </div>
  );
}
